/**
 * Onboarding.js
 *
 * WHY STEP COMPONENTS ARE DEFINED OUTSIDE Onboarding()
 * ─────────────────────────────────────────────────────
 * All Step* components (StepPersonal, StepAddress, etc.) are declared at
 * module scope — NOT inside the Onboarding function body.
 *
 * If they were defined inside Onboarding, React would create a brand-new
 * function reference for each component on every render (every keystroke).
 * React uses referential identity to decide whether to remount:
 *   old StepPersonal !== new StepPersonal  →  unmount + remount
 *   → every mounted <input> is destroyed and recreated → focus lost after 1 char
 *
 * By keeping them outside, their identity never changes between renders,
 * so React reuses the existing DOM and focus is preserved.
 * Data flows in via props: { formData, handleInputChange, ... }
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  FileText,
  Mail,
  RefreshCw,
  Search,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { uploadRequestConfig } from '../../services/api';
import { MAX_FILE_MB, MAX_FILE_SIZE_BYTES } from '../../config/config';
import LoadingSpinner from '../common/LoadingSpinner';
import DocumentUploadModal from './DocumentUploadModal';
import { toast } from 'react-toastify';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';
import {
  derivePayrollValues,
  formatCurrencyPreview,
  PAYROLL_PERCENTAGE_FIELDS,
  PAYROLL_PERCENTAGE_INPUT_FIELDS,
} from '../../utils/payrollUtils';
import { ORGANIZATION_OPTIONS } from '../../constants/organizations';
import {
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_OTHER,
  DOCUMENT_LABEL_MAX_LENGTH,
  DOCUMENT_LABEL_MIN_LENGTH,
  documentDisplayName,
} from '../../constants/documentTypes';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS  (module-scope — available to all components below)
// ─────────────────────────────────────────────────────────────────────────────

const INITIAL_FORM_DATA = {
  // Step 1 – Personal
  organization:            '',
  employeeId:              '',
  name:                    '',
  dateOfBirth:             '',
  gender:                  '',
  maritalStatus:           '',
  jobTitle:                '',
  joinDate:                '',
  password:                '',
  physicalChallenged:      false,
  isIntern:                false,
  internshipDays:          90,
  bloodGroup:              '',
  panNumber:               '',
  aadhaarNumber:           '',
  passportNumber:          '',

  // Step 2 – Address & Contact
  email:                   '',
  mobileNumber:            '',
  alternateNumber:         '',
  emergencyContactNumber:  '',
  emergencyName:           '',
  emergencyRelation:       '',
  presentAddress:          '',
  permanentAddress:        '',
  city:                    '',
  state:                   '',
  pinCode:                 '',

  // Step 3 – Payroll
  ctcBasic:                '',
  ctcHRAPercentage:        '',
  ctcHRA:                  '',
  ctcSpecialAllowancePercentage: '',
  ctcSpecialAllowance:     '',
  ctcTravelAllowancePercentage: '',
  ctcTravelAllowance:      '',
  ctcITAllowancePercentage: '',
  ctcITAllowance:          '',
  payScale:                '',
  pfDetails:               '',
  medicalInsurance:        '',

  // Step 4 – Education (all optional)
  schoolName:                       '',
  schoolBoard:                      '',
  schoolYearOfGraduation:           '',
  schoolPercentageOrCGPA:           '',
  universityName:                   '',
  universityBoard:                  '',
  universityYearOfGraduation:       '',
  universityPercentageOrCGPA:       '',
  postGraduationName:               '',
  postGraduationBoard:              '',
  postGraduationYearOfGraduation:   '',
  postGraduationPercentageOrCGPA:   '',
  highestEducationCategory:         '',
  highestEducationInstitutionName:  '',
  highestEducationBoard:            '',
  highestEducationYearOfPassing:    '',
  highestEducationPercentageOrCGPA: '',

  // Step 5 – Experience & References (all optional)
  totalExperience:            '',
  previousCompanyName:        '',
  previousCompanyLocation:    '',
  previousJobTitle:           '',
  previousSupervisorName:     '',
  previousSupervisorContact:  '',
  previousStartDate:          '',
  previousEndDate:            '',
  reasonForLeaving:           '',
  relievingLetterSubmitted:   false,
  reference1Name:             '',
  reference1Address:          '',
  reference1Mobile:           '',
  reference1Relationship:     '',
  reference2Name:             '',
  reference2Address:          '',
  reference2Mobile:           '',
  reference2Relationship:     '',

  // Step 6 – Reporting Manager
  reportingManagerEmployeeId: '',
};

// Cast to Number() when building API payload
const NON_NEGATIVE_NUMBER_FIELDS = [
  'ctcBasic', 'ctcHRA', 'ctcSpecialAllowance',
  'ctcTravelAllowance', 'ctcITAllowance', 'payScale', 'internshipDays',
  // totalExperience excluded — backend validates as string
];
const YEAR_FIELDS = [
  'schoolYearOfGraduation', 'universityYearOfGraduation',
  'postGraduationYearOfGraduation', 'highestEducationYearOfPassing',
];
const NUMERIC_FIELDS = [...NON_NEGATIVE_NUMBER_FIELDS, ...YEAR_FIELDS];

const STEPS = [
  {
    id: 1,
    label: 'Personal',
    description: 'Identity & credentials',
    requiredFields: [
      'organization', 'name', 'dateOfBirth', 'gender', 'maritalStatus',
      'jobTitle', 'password', 'panNumber', 'aadhaarNumber',
    ],
  },
  {
    id: 2,
    label: 'Address',
    description: 'Contact & location',
    requiredFields: [
      'email', 'mobileNumber', 'alternateNumber',
      'emergencyName', 'emergencyRelation', 'emergencyContactNumber',
      'presentAddress', 'permanentAddress', 'city', 'state', 'pinCode',
    ],
  },
  {
    id: 3,
    label: 'Payroll',
    description: 'Monthly CTC & compliance',
    requiredFields: [
      'ctcBasic', 'ctcHRA', 'ctcSpecialAllowance',
      'ctcTravelAllowance', 'ctcITAllowance',
    ],
  },
  {
    id: 4,
    label: 'Education',
    description: 'Academic background',
    requiredFields: [],
  },
  {
    id: 5,
    label: 'Experience',
    description: 'Work history & references',
    requiredFields: [],
  },
  {
    id: 6,
    label: 'Reporting Manager',
    description: 'Assign a manager (optional)',
    requiredFields: [],
  },
  {
    id: 7,
    label: 'Documents',
    description: 'Upload employee documents',
    requiredFields: [],
  },
];

const FIELD_LABELS = {
  organization: 'Organization',
  name: 'Name', dateOfBirth: 'Date of Birth', gender: 'Gender',
  maritalStatus: 'Marital Status', jobTitle: 'Job Title',
  employeeId: 'Temp ID', panNumber: 'PAN Number',
  aadhaarNumber: 'Aadhaar Number', password: 'Password',
  email: 'Email', mobileNumber: 'Mobile Number',
  alternateNumber: 'Alternate Number',
  emergencyContactNumber: 'Emergency Contact Number',
  presentAddress: 'Present Address', permanentAddress: 'Permanent Address',
  city: 'City', state: 'State', pinCode: 'Pin Code',
  ctcBasic: 'Basic salary (monthly)',
  ctcHRAPercentage: 'HRA Percentage', ctcHRA: 'HRA (monthly)',
  ctcSpecialAllowancePercentage: 'Special Allowance Percentage',
  ctcSpecialAllowance: 'Special allowance (monthly)',
  ctcTravelAllowancePercentage: 'Travel Allowance Percentage',
  ctcTravelAllowance: 'Travel allowance (monthly)',
  ctcITAllowancePercentage: 'IT Allowance Percentage',
  ctcITAllowance: 'IT allowance (monthly)',
};

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500';

const OFFER_LETTER_MAX_BYTES = MAX_FILE_SIZE_BYTES;
const OFFER_LETTER_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

function validateOfferLetterFile(file) {
  if (!file) return 'Please select a file';
  const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
  if (!allowed.includes(file.type)) return 'Only PDF, JPG, and PNG files are allowed';
  if (file.size > OFFER_LETTER_MAX_BYTES) return `File size must be ${MAX_FILE_MB}MB or less`;
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// STEPPER HEADER  (module-scope — stable identity, no focus issues)
// ─────────────────────────────────────────────────────────────────────────────
const StepHeader = ({ currentStep }) => (
  <div className="flex items-center px-6 py-4 border-b border-slate-200 bg-slate-50">
    {STEPS.map((step, idx) => {
      const done    = currentStep > step.id;
      const current = currentStep === step.id;
      return (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center gap-1">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-all ${
              done    ? 'bg-emerald-500 text-white' :
              current ? 'bg-primary-600 text-white shadow-md shadow-primary-200' :
                        'bg-slate-200 text-slate-500'
            }`}>
              {done ? <CheckCircle2 className="h-5 w-5" /> : step.id}
            </div>
            <span className={`hidden sm:block text-xs font-medium text-center w-16 truncate ${
              current ? 'text-primary-700' : done ? 'text-emerald-600' : 'text-slate-400'
            }`}>
              {step.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`h-0.5 flex-1 mx-2 rounded-full transition-all ${
              currentStep > step.id ? 'bg-emerald-400' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 – PERSONAL
// ─────────────────────────────────────────────────────────────────────────────
const StepPersonal = ({
  formData,
  handleInputChange,
  handleOrganizationChange,
  signedOfferLetterFile,
  onSignedOfferLetterChange,
}) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <FileText className="h-4 w-4 text-slate-500" aria-hidden />
        Signed offer letter
      </label>
      <input
        type="file"
        accept={OFFER_LETTER_ACCEPT}
        className={inputCls}
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          e.target.value = '';
          if (!file) {
            onSignedOfferLetterChange(null);
            return;
          }
          const err = validateOfferLetterFile(file);
          if (err) {
            toast.error(err);
            onSignedOfferLetterChange(null);
            return;
          }
          onSignedOfferLetterChange(file);
        }}
      />
      {/* <p className="mt-1 text-xs text-slate-500">Optional. PDF or image, max 5MB.</p> */}
      {signedOfferLetterFile && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-700">
          <span className="truncate font-medium" title={signedOfferLetterFile.name}>{signedOfferLetterFile.name}</span>
          <button
            type="button"
            onClick={() => onSignedOfferLetterChange(null)}
            className="shrink-0 text-rose-600 hover:underline"
          >
            Remove
          </button>
        </div>
      )}
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Organization *</label>
      <select
        value={formData.organization}
        onChange={(e) => handleOrganizationChange(e.target.value)}
        className={inputCls}
      >
        <option value="">Select organization</option>
        {ORGANIZATION_OPTIONS.map((org) => <option key={org} value={org}>{org}</option>)}
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        Temp ID
      </label>
      <input
        type="text"
        readOnly
        value=""
        className={`${inputCls} cursor-not-allowed bg-slate-50 text-slate-400`}
        placeholder="Auto-generated on submit (e.g. TEMP-123456)"
      />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Full Name *</label>
      <input type="text" required value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Date of Birth *</label>
      <input type="date" required value={formData.dateOfBirth}
        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Gender *</label>
      <select required value={formData.gender}
        onChange={(e) => handleInputChange('gender', e.target.value)} className={inputCls}>
        <option value="">Select gender</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="other">Other</option>
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Marital Status *</label>
      <select required value={formData.maritalStatus}
        onChange={(e) => handleInputChange('maritalStatus', e.target.value)} className={inputCls}>
        <option value="">Select status</option>
        <option value="single">Single</option>
        <option value="married">Married</option>
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Job Title *</label>
      <input type="text" required value={formData.jobTitle}
        onChange={(e) => handleInputChange('jobTitle', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Join Date</label>
      <input type="date" value={formData.joinDate}
        onChange={(e) => handleInputChange('joinDate', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        Password * <span className="text-xs text-slate-400">(min 6 chars)</span>
      </label>
      <input type="password" required minLength={6} value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">PAN Number *</label>
      <input type="text" required value={formData.panNumber}
        onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
        className={inputCls} placeholder="ABCDE1234F" />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Aadhaar Number *</label>
      <input type="text" required value={formData.aadhaarNumber}
        onChange={(e) => handleInputChange('aadhaarNumber', e.target.value)}
        className={inputCls} placeholder="12-digit number" />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Passport Number</label>
      <input type="text" value={formData.passportNumber}
        onChange={(e) => handleInputChange('passportNumber', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Blood Group</label>
      <select value={formData.bloodGroup}
        onChange={(e) => handleInputChange('bloodGroup', e.target.value)} className={inputCls}>
        <option value="">Select</option>
        {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map((bg) =>
          <option key={bg} value={bg}>{bg}</option>)}
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">&nbsp;</label>
      <div className="flex h-[42px] items-center gap-3">
        <input
          id="physicalChallenged"
          type="checkbox"
          checked={formData.physicalChallenged}
          onChange={(e) => handleInputChange('physicalChallenged', e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="physicalChallenged" className="text-sm font-medium text-slate-700 cursor-pointer">
          Physical Challenged
        </label>
      </div>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        Internship <span className="text-xs text-slate-400">(optional)</span>
      </label>
      <div className="flex h-[42px] items-center gap-3">
        <input
          id="isIntern"
          type="checkbox"
          checked={formData.isIntern}
          onChange={(e) => handleInputChange('isIntern', e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="isIntern" className="text-sm font-medium text-slate-700 cursor-pointer">
          Intern
        </label>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Interns are exempt from the ₹200 professional tax until HR confirms them.
      </p>
    </div>

    {formData.isIntern && (
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Internship Days</label>
        <input
          type="number"
          min="0"
          step="1"
          value={formData.internshipDays}
          onChange={(e) => handleInputChange('internshipDays', e.target.value)}
          className={inputCls}
        />
        <p className="mt-1 text-xs text-slate-400">Counted from the join date. Default 90 days.</p>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 – ADDRESS & CONTACT
// ─────────────────────────────────────────────────────────────────────────────
const StepAddress = ({ formData, handleInputChange }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    {[
      ['email',                  'Email *',              'email'],
      ['mobileNumber',           'Mobile Number *',      'tel'],
      ['alternateNumber',        'Alternate Number *',   'tel'],

      ['emergencyName', 'Emergency Name *', 'text'],
      ['emergencyRelation', 'Emergency Relation *', 'text'],
      ['emergencyContactNumber', 'Emergency Contact *', 'tel'],
      
      ['city',                   'City *',               'text'],
      ['state',                  'State *',              'text'],
      ['pinCode',                'Pin Code *',           'text'],

    ].map(([field, label, type]) => (
      <div key={field}>
        <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
        <input
          type={type}
          required
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={inputCls}
        />
      </div>
    ))}

    <div className="md:col-span-3">
      <label className="mb-1 block text-sm font-medium text-slate-700">Present Address *</label>
      <textarea required rows={2} value={formData.presentAddress}
        onChange={(e) => handleInputChange('presentAddress', e.target.value)} className={inputCls} />
    </div>

    <div className="md:col-span-3">
      <label className="mb-1 block text-sm font-medium text-slate-700">Permanent Address *</label>
      <textarea required rows={2} value={formData.permanentAddress}
        onChange={(e) => handleInputChange('permanentAddress', e.target.value)} className={inputCls} />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 3 – PAYROLL
// ─────────────────────────────────────────────────────────────────────────────
const StepPayroll = ({ formData, handleInputChange }) => {
  const annualEquivalent =
    formData.payScale !== '' && formData.payScale != null && Number.isFinite(Number(formData.payScale))
      ? formatCurrencyPreview(Number(formData.payScale) * 12)
      : null;

  return (
  <div className="space-y-4">
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-medium">All CTC amounts are monthly (₹ / month).</p>
      <p className="mt-1 text-amber-800/90">
        If monthly pay is ₹40,000, enter 40000 — do not multiply by 12. Offer-letter annual CTC can be checked below as a display-only equivalent.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Basic salary (₹ / month) *</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Rs</span>
        <input
          type="number"
          min="0"
          step="0.01"
          required
          value={formData.ctcBasic}
          onChange={(e) => handleInputChange('ctcBasic', e.target.value)}
          className={`${inputCls} pl-10`}
          placeholder="e.g. 20000"
        />
      </div>
      <p className="mt-1 text-xs text-slate-500">Monthly basic only. Allowances are % of this amount.</p>
    </div>

    {PAYROLL_PERCENTAGE_FIELDS.map(({ percentageField, amountField, label }) => (
      <div key={percentageField} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <label className="mb-1 block text-sm font-medium text-slate-700">{label} % of monthly basic *</label>
        <div className="relative">
          <input
            type="number"
            min="0"
            step="0.01"
            required
            value={formData[percentageField]}
            onChange={(e) => handleInputChange(percentageField, e.target.value)}
            className={`${inputCls} pr-9 bg-white`}
            placeholder="e.g. 30"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
        </div>
        <p className="mt-2 text-xs font-medium text-slate-600">
          Monthly amount: <span className="text-slate-900">{formatCurrencyPreview(formData[amountField])}</span>
        </p>
      </div>
    ))}

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Total CTC (₹ / month)</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Rs</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={formData.payScale}
          readOnly
          className={`${inputCls} pl-10 cursor-not-allowed bg-slate-100 text-slate-700`}
        />
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Auto-calculated: monthly basic + all monthly allowances.
        {annualEquivalent ? (
          <> Annual equivalent (display only): <span className="font-medium text-slate-700">{annualEquivalent}</span>.</>
        ) : null}
      </p>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">PF Details</label>
      <input type="text" value={formData.pfDetails}
        onChange={(e) => handleInputChange('pfDetails', e.target.value)}
        className={inputCls} placeholder="UAN / account number" />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Medical Insurance</label>
      <input type="text" value={formData.medicalInsurance}
        onChange={(e) => handleInputChange('medicalInsurance', e.target.value)}
        className={inputCls} placeholder="Policy number / provider" />
    </div>
    </div>
  </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 4 – EDUCATION  (all optional)
// ─────────────────────────────────────────────────────────────────────────────
const StepEducation = ({ formData, handleInputChange }) => (
  <div className="space-y-5">
    {[
      {
        heading: 'School (10th / 12th)',
        fields: [
          ['schoolName',              'School Name',        'text'],
          ['schoolBoard',             'Board',              'text'],
          ['schoolYearOfGraduation',  'Year of Graduation', 'number'],
          ['schoolPercentageOrCGPA',  'Percentage / CGPA',  'text'],
        ],
      },
      {
        heading: 'Graduation (UG)',
        fields: [
          ['universityName',              'University Name',     'text'],
          ['universityBoard',             'Board / Affiliation', 'text'],
          ['universityYearOfGraduation',  'Year of Graduation',  'number'],
          ['universityPercentageOrCGPA',  'Percentage / CGPA',   'text'],
        ],
      },
      {
        heading: 'Post Graduation (PG)',
        fields: [
          ['postGraduationName',              'Course / University', 'text'],
          ['postGraduationBoard',             'Board / Affiliation', 'text'],
          ['postGraduationYearOfGraduation',  'Year of Graduation',  'number'],
          ['postGraduationPercentageOrCGPA',  'Percentage / CGPA',   'text'],
        ],
      },
      {
        heading: 'Highest Education',
        fields: [
          ['highestEducationCategory',         'Category',          'text'],
          ['highestEducationInstitutionName',   'Institution',       'text'],
          ['highestEducationBoard',             'Board',             'text'],
          ['highestEducationYearOfPassing',     'Year of Passing',   'number'],
          ['highestEducationPercentageOrCGPA',  'Percentage / CGPA', 'text'],
        ],
      },
    ].map((section) => (
      <div key={section.heading} className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {section.heading}
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {section.fields.map(([field, label, type]) => (
            <div key={field}>
              <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
              <input
                type={type}
                min={type === 'number' ? 1900 : undefined}
                max={type === 'number' ? 2100 : undefined}
                value={formData[field]}
                onChange={(e) => handleInputChange(field, e.target.value)}
                className={inputCls}
              />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 5 – EXPERIENCE & REFERENCES  (all optional)
// ─────────────────────────────────────────────────────────────────────────────
const StepExperience = ({ formData, handleInputChange }) => (
  <div className="space-y-5">

    <div className="rounded-lg border border-slate-200 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Previous Employment
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {[
          ['totalExperience',           'Total Experience',   'text'],
          ['previousCompanyName',       'Company Name',       'text'],
          ['previousCompanyLocation',   'Location',           'text'],
          ['previousJobTitle',          'Job Title',          'text'],
          ['previousSupervisorName',    'Supervisor Name',    'text'],
          ['previousSupervisorContact', 'Supervisor Contact', 'text'],
          ['reasonForLeaving',          'Reason for Leaving', 'text'],
        ].map(([field, label, type]) => (
          <div key={field}>
            <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
            <input
              type={type}
              value={formData[field]}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className={inputCls}
            />
          </div>
        ))}

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Start Date</label>
          <input type="date" value={formData.previousStartDate}
            onChange={(e) => handleInputChange('previousStartDate', e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">End Date</label>
          <input type="date" value={formData.previousEndDate}
            onChange={(e) => handleInputChange('previousEndDate', e.target.value)} className={inputCls} />
        </div>

        <div className="flex items-center gap-2 pt-6">
          <input
            id="relieving"
            type="checkbox"
            checked={formData.relievingLetterSubmitted}
            onChange={(e) => handleInputChange('relievingLetterSubmitted', e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="relieving" className="text-xs font-medium text-slate-700">
            Relieving Letter Submitted
          </label>
        </div>
      </div>
    </div>

    {[
      { prefix: 'reference1', heading: 'Reference 1' },
      { prefix: 'reference2', heading: 'Reference 2' },
    ].map(({ prefix, heading }) => (
      <div key={prefix} className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{heading}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {['Name', 'Address', 'Mobile', 'Relationship'].map((label) => {
            const field = `${prefix}${label}`;
            return (
              <div key={field}>
                <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
                <input type="text" value={formData[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)} className={inputCls} />
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 6 – REPORTING MANAGER
// ─────────────────────────────────────────────────────────────────────────────
const StepReportingManager = ({ formData, handleInputChange }) => (
  <div className="max-w-md">
    <label className="mb-1 block text-sm font-medium text-slate-700">Reporting Manager's Employee ID</label>
    <input
      type="text"
      value={formData.reportingManagerEmployeeId}
      onChange={(e) => handleInputChange('reportingManagerEmployeeId', e.target.value.toUpperCase())}
      className={inputCls}
      placeholder="e.g. PFS0006"
    />
    <p className="mt-2 text-xs text-slate-500">
      Optional. Enter the Employee ID of the person this new employee reports to. Leave blank to assign one later.
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 7 – DOCUMENTS
// ─────────────────────────────────────────────────────────────────────────────

const OtherDocumentRow = ({
  employeeId,
  rowKey,
  row,
  setRowState,
  fileInputsRef,
  onUpload,
  onSave,
  uploadedDocs,
}) => {
  const [label, setLabel] = React.useState(row?.documentLabel || '');

  React.useEffect(() => {
    if (row?.documentLabel !== undefined && row.documentLabel !== label) {
      setLabel(row.documentLabel || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.documentLabel]);

  const savedOther = uploadedDocs.find((d) => d?.documentType === DOCUMENT_TYPE_OTHER && d?.isActive);
  const isUploaded = Boolean(row?.tempUploadId);
  const isSaved = Boolean(savedOther);
  const disabledUpload = Boolean(row?.uploading || row?.saving);
  const disabledSave = Boolean(row?.saving || row?.uploading || !row?.tempUploadId || isSaved);

  return (
    <div className={`flex items-center gap-4 px-5 py-4 ${isSaved || isUploaded ? 'bg-emerald-50' : ''}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isSaved || isUploaded ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
          {isSaved || isUploaded ? '✓' : '📄'}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">Other document</p>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            <input
              type="text"
              value={label}
              onChange={(e) => {
                const v = e.target.value;
                setLabel(v);
                setRowState(rowKey, { documentLabel: v });
              }}
              placeholder={`Enter document name (${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} chars)`}
              maxLength={DOCUMENT_LABEL_MAX_LENGTH}
              className={inputCls}
            />
            <p className="text-xs text-slate-500 md:col-span-2">
              {savedOther
                ? `${savedOther.documentLabel || savedOther.originalFileName || 'Other document'}`
                : row?.tempUploadId
                  ? `${row.tempFileName || row.fileName || 'File'} (uploaded)`
                  : 'No file uploaded yet'}
            </p>
            {(row?.uploadError || row?.saveError) && (
              <p className="text-xs text-rose-600 md:col-span-2">{row.uploadError || row.saveError}</p>
            )}
          </div>
        </div>
      </div>

      <input
        ref={(el) => { fileInputsRef.current[rowKey] = el; }}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          onUpload(rowKey, DOCUMENT_TYPE_OTHER, label, file);
          e.target.value = '';
        }}
      />

      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          disabled={disabledUpload}
          onClick={() => fileInputsRef.current[rowKey]?.click()}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 min-w-[110px]"
        >
          {row?.uploading ? 'Uploading…' : (isSaved || isUploaded ? 'Replace' : 'Upload')}
        </button>
        <button
          type="button"
          disabled={disabledSave}
          onClick={() => onSave(rowKey, DOCUMENT_TYPE_OTHER, label)}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 min-w-[110px]"
        >
          {row?.saving ? 'Saving…' : (isSaved ? 'Saved' : 'Save')}
        </button>
      </div>
    </div>
  );
};

const StepDocuments = React.forwardRef(({ employeeId, employeeName, jobTitle, tempEmployeeId }, ref) => {
  const fileInputsRef = React.useRef({});
  const [uploadedDocs, setUploadedDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState({});
  const [openingDocId, setOpeningDocId] = React.useState(null);
  const [documentTypes, setDocumentTypes] = React.useState(DOCUMENT_TYPE_OPTIONS);
  const [rows, setRows] = React.useState({});
  const rowsRef = React.useRef(rows);

  React.useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  React.useEffect(() => {
    const fetchDocTypes = async () => {
      try {
        const params = jobTitle ? `?jobTitle=${encodeURIComponent(jobTitle)}` : '';
        const res = await api.get(`/documents/required-types${params}`);
        const types = res.data?.documentTypes || res.data?.types || res.data;
        if (Array.isArray(types) && types.length > 0) {
          const hasOther = types.some((t) => t && t.value === DOCUMENT_TYPE_OTHER);
          setDocumentTypes(hasOther ? types : [...types, DOCUMENT_TYPE_OPTIONS.find((o) => o.value === DOCUMENT_TYPE_OTHER)]);
        } else {
          setDocumentTypes(DOCUMENT_TYPE_OPTIONS);
        }
      } catch {
        setDocumentTypes(DOCUMENT_TYPE_OPTIONS);
      }
    };
    fetchDocTypes();
  }, [jobTitle]);

  React.useEffect(() => {
    if (!employeeId) return;
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/documents/employee/${employeeId}`);
        setUploadedDocs(response.data.documents || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
        toast.error('Failed to load documents');
        setUploadedDocs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [employeeId]);

  const refreshDocuments = React.useCallback(async () => {
    if (!employeeId) return;
    try {
      const response = await api.get(`/documents/employee/${employeeId}`);
      setUploadedDocs(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  }, [employeeId]);

  const validateFile = (file) => {
    if (!file) return 'Please select a file';
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) return 'Only PDF, JPG, and PNG files allowed';
    if (file.size > MAX_FILE_SIZE_BYTES) return `File size must be less than ${MAX_FILE_MB}MB`;
    return null;
  };

  const setRowState = (key, patch) => {
    setRows((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...patch,
      },
    }));
  };

  const saveTempUpload = async ({ key, tempUploadId, docType, docLabel }) => {
    const payload = {
      tempUploadId,
      documentType: docType,
    };
    if (docType === DOCUMENT_TYPE_OTHER) payload.documentLabel = String(docLabel || '').trim();

    const response = await api.post(`/documents/employee/${employeeId}/save-upload`, payload);
    const saved = response.data?.document;

    setRowState(key, {
      saving: false,
      saveError: '',
      uploadError: '',
      savedDocumentId: saved?._id || '',
      tempUploadId: '',
    });
    await refreshDocuments();
    return saved;
  };

  React.useImperativeHandle(ref, () => ({
    saveAllPending: async () => {
      const snapshot = rowsRef.current || {};
      const pending = Object.entries(snapshot)
        .filter(([, r]) => r && r.tempUploadId)
        .map(([key, r]) => ({ key, row: r }));

      if (pending.length === 0) return { saved: 0, failed: 0 };

      let savedCount = 0;
      let failedCount = 0;

      for (const item of pending) {
        try {
          setRowState(item.key, { saving: true, saveError: '' });
          await saveTempUpload({
            key: item.key,
            tempUploadId: item.row.tempUploadId,
            docType: item.row.docType || (item.key === '__other__' ? DOCUMENT_TYPE_OTHER : item.key),
            docLabel: item.row.documentLabel || '',
          });
          savedCount += 1;
        } catch (error) {
          console.error('Auto-save document error:', error);
          setRowState(item.key, { saving: false, saveError: error?.response?.data?.message || 'Save failed' });
          failedCount += 1;
        }
      }

      return { saved: savedCount, failed: failedCount };
    },
  }));

  const handleSelectAndUpload = async (key, docType, docLabel, file) => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    if (docType === DOCUMENT_TYPE_OTHER) {
      const t = String(docLabel || '').trim();
      if (t.length < DOCUMENT_LABEL_MIN_LENGTH) {
        toast.error(`Enter a document name (${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} characters)`);
        return;
      }
      if (t.length > DOCUMENT_LABEL_MAX_LENGTH) {
        toast.error(`Document name must be at most ${DOCUMENT_LABEL_MAX_LENGTH} characters`);
        return;
      }
    }

    setRowState(key, { uploading: true, uploadError: '', saveError: '', fileName: file.name, fileSize: file.size, docType, documentLabel: docLabel || '' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('documentType', docType);

      const response = await api.post(
        `/documents/employee/${employeeId}/upload-temp`,
        fd,
        uploadRequestConfig(),
      );

      const temp = response.data?.tempUpload || response.data?.temp;
      const tempUploadId = temp?._id || response.data?.tempUploadId;
      if (!tempUploadId) throw new Error('Missing tempUploadId from server');

      setRowState(key, {
        uploading: false,
        uploadError: '',
        saveError: '',
        tempUploadId,
        tempFileName: temp?.originalFileName || file.name,
        uploadedAt: Date.now(),
        docType,
        documentLabel: docLabel || '',
      });
      toast.success('File uploaded. Click Save to confirm.');
    } catch (error) {
      console.error('Temp upload error:', error);
      setRowState(key, { uploading: false, uploadError: error?.response?.data?.message || 'Upload failed' });
      toast.error(error?.response?.data?.message || 'Upload failed');
    }
  };

  const handleSaveRow = async (key, docType, docLabel) => {
    const row = rows[key];
    if (!row?.tempUploadId) {
      toast.error('Please upload a file first');
      return;
    }
    if (docType === DOCUMENT_TYPE_OTHER) {
      const t = String(docLabel || '').trim();
      if (t.length < DOCUMENT_LABEL_MIN_LENGTH) {
        toast.error(`Enter a document name (${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} characters)`);
        return;
      }
    }

    setRowState(key, { saving: true, saveError: '' });
    try {
      await saveTempUpload({
        key,
        tempUploadId: row.tempUploadId,
        docType,
        docLabel,
      });
      toast.success('Document saved');
    } catch (error) {
      console.error('Save upload error:', error);
      setRowState(key, { saving: false, saveError: error?.response?.data?.message || 'Save failed' });
      toast.error(error?.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (docId, titleForConfirm) => {
    if (!window.confirm(`Delete “${titleForConfirm}” document?`)) return;
    setDeleting((prev) => ({ ...prev, [docId]: true }));
    try {
      await api.delete(`/documents/${docId}`);
      setUploadedDocs((prev) => prev.filter((d) => d._id !== docId));
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleting((prev) => ({ ...prev, [docId]: false }));
    }
  };

  const handleViewDocument = async (docId) => {
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.opener = null;
      try {
        previewWindow.document.write('<title>Opening document...</title><p style="font-family: sans-serif; padding: 16px;">Opening document...</p>');
        previewWindow.document.close();
      } catch {
        // Ignore write failures and continue with redirect once the signed URL is ready.
      }
    }
    setOpeningDocId(docId);
    try {
      const response = await api.get(`/documents/${docId}/access-url`);
      const accessUrl = response.data?.accessUrl;

      if (!accessUrl) {
        throw new Error('Missing signed access URL');
      }

      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.replace(accessUrl);
      } else {
        window.location.assign(accessUrl);
      }
    } catch (error) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      console.error('Document access error:', error);
      toast.error('Failed to open document');
    } finally {
      setOpeningDocId(null);
    }
  };

  if (!employeeId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <p className="text-sm text-amber-800">Employee is being created...</p>
      </div>
    );
  }

  const requiredList = (documentTypes || []).filter((t) => t && t.value !== DOCUMENT_TYPE_OTHER);
  const otherType = (documentTypes || []).find((t) => t && t.value === DOCUMENT_TYPE_OTHER);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h3 className="mb-2 text-sm font-semibold text-blue-900">Upload Employee Documents</h3>
        <p className="text-xs text-blue-700">
          Employee <strong>{employeeName}</strong> (Temp ID: <strong>{tempEmployeeId || 'Pending assignment'}</strong>) created.
          Upload each required document row-wise, then click <strong>Save</strong> to confirm it.
        </p>
      </div>

      {/* Row-wise required documents (like image2) */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-900">Document Upload</h3>
          <p className="mt-1 text-xs text-slate-500">Upload all required documents. Files accepted: PDF, JPG, PNG (max {MAX_FILE_MB}MB).</p>
        </div>

        <div className="divide-y divide-slate-100">
          {requiredList.map((t) => {
            const key = t.value;
            const row = rows[key] || {};
            const saved = uploadedDocs.find((d) => d?.documentType === t.value && d?.isActive);
            const isUploaded = Boolean(row.tempUploadId);
            const isSaved = Boolean(saved);
            const disabledUpload = Boolean(row.uploading || row.saving);
            const disabledSave = Boolean(row.saving || row.uploading || !row.tempUploadId || isSaved);

            return (
              <div key={t.value} className={`flex items-center gap-4 px-5 py-4 ${isSaved || isUploaded ? 'bg-emerald-50' : ''}`}>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isSaved || isUploaded ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {isSaved || isUploaded ? '✓' : '📄'}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {t.label} <span className="text-rose-500">*</span>
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 truncate">
                      {saved
                        ? `${saved.originalFileName || 'Document'}`
                        : row.tempUploadId
                          ? `${row.tempFileName || row.fileName || 'File'} (uploaded)`
                          : 'No file uploaded yet'}
                    </p>
                    {(row.uploadError || row.saveError) && (
                      <p className="mt-1 text-xs text-rose-600">{row.uploadError || row.saveError}</p>
                    )}
                  </div>
                </div>

                <input
                  ref={(el) => { fileInputsRef.current[key] = el; }}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    handleSelectAndUpload(key, t.value, '', file);
                    e.target.value = '';
                  }}
                />

                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    type="button"
                    disabled={disabledUpload}
                    onClick={() => fileInputsRef.current[key]?.click()}
                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 min-w-[110px]"
                  >
                    {row.uploading ? 'Uploading…' : (isSaved || isUploaded ? 'Replace' : 'Upload')}
                  </button>
                  <button
                    type="button"
                    disabled={disabledSave}
                    onClick={() => handleSaveRow(key, t.value, '')}
                    className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 min-w-[110px]"
                  >
                    {row.saving ? 'Saving…' : (isSaved ? 'Saved' : 'Save')}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Optional "Other" row (custom label) */}
          {otherType && (
            <OtherDocumentRow
              employeeId={employeeId}
              rowKey="__other__"
              row={rows.__other__ || {}}
              setRowState={setRowState}
              fileInputsRef={fileInputsRef}
              onUpload={handleSelectAndUpload}
              onSave={handleSaveRow}
              uploadedDocs={uploadedDocs}
              refreshDocuments={refreshDocuments}
            />
          )}
        </div>
      </div>

      {/* Uploaded Documents List */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900 flex items-center text-sm">
          Uploaded Documents
          {uploadedDocs.length > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700">
              {uploadedDocs.length}
            </span>
          )}
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <p className="text-sm text-slate-500">Loading documents...</p>
          </div>
        ) : uploadedDocs.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center">
            <p className="text-sm text-slate-500">
              No documents uploaded yet. Add documents above and click Save All.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {uploadedDocs.map((doc) => (
              <div
                key={doc._id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <span className="text-lg">📄</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {documentDisplayName(doc, documentTypes)}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{doc.originalFileName}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {(doc.size / 1024).toFixed(2)} KB &middot; Uploaded
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleViewDocument(doc._id)}
                    disabled={openingDocId === doc._id}
                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                    title="View document"
                  >
                    {openingDocId === doc._id ? '⏳' : '👁️'}
                  </button>
                  <button
                    onClick={() => handleDelete(doc._id, documentDisplayName(doc, documentTypes))}
                    disabled={deleting[doc._id]}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

const STEP_PANELS = [StepPersonal, StepAddress, StepPayroll, StepEducation, StepExperience, StepReportingManager, StepDocuments];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ManualOnboarding = () => {

  const newEmployeeIdRef = React.useRef(null);
  const newEmployeeNameRef = React.useRef(null);

  const [employees, setEmployees]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showFormModal, setShowFormModal]               = useState(false);
  const [currentStep, setCurrentStep]                   = useState(1);
  const [submitting, setSubmitting]                     = useState(false);
  const [formData, setFormData]                         = useState(INITIAL_FORM_DATA);
  const [signedOfferLetterFile, setSignedOfferLetterFile] = useState(null);

  const [showDocumentModal, setShowDocumentModal]       = useState(false);
  const [newEmployeeId, setNewEmployeeId]               = useState(null);
  const [newEmployeeName, setNewEmployeeName]           = useState('');
  const [newEmployeeTempId, setNewEmployeeTempId]       = useState('');
  const documentsStepRef = React.useRef(null);

  // ── 1. Fetch employee list ───────────────────────────────────
  const fetchEmployees = async () => {
    try {
      const response = await api.get('/admin/employees', {
        params: { onboardingStatus: 'pending' }
      });
      setEmployees(normalizeUserProfiles(response.data.employees || []));
    } catch (error) {
      console.error('fetchEmployees error:', error);
      toast.error('Failed to load employee records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);

  // ✅ FIX 1: REMOVED the useEffect that watched newEmployeeId and called
  // setCurrentStep(6) via setTimeout. That caused Step 6 to render first
  // with newEmployeeId=null (showing the amber banner), then re-render 100ms
  // later. Since handleNext already calls setCurrentStep(6) directly alongside
  // setNewEmployeeId(), React 18 batches them into one render — Step 6 always
  // gets the real employeeId on its very first paint.

  // ── 2. Modal open / close ────────────────────────────────────
  const openCreateModal = () => {
    setFormData(INITIAL_FORM_DATA);
    setSignedOfferLetterFile(null);
    setCurrentStep(1);
    setShowFormModal(true);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setNewEmployeeId(null);
    setNewEmployeeName('');
    setNewEmployeeTempId('');
    setLoading(true);
    fetchEmployees();
  };

  const closeCreateModal = () => {
    setShowFormModal(false);
    setFormData(INITIAL_FORM_DATA);
    setSignedOfferLetterFile(null);
    setCurrentStep(1);
    setNewEmployeeId(null);
    setNewEmployeeName('');
    setNewEmployeeTempId('');
    newEmployeeIdRef.current = null;
    newEmployeeNameRef.current = null;
  };

  // ── 3. Generic input change handler ─────────────────────────
  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'ctcBasic' || PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(field)) {
        return { ...next, ...derivePayrollValues(next) };
      }
      return next;
    });
  };

  // ── 4. Organization select ───────────────
  const handleOrganizationChange = async (organization) => {
    setFormData((prev) => ({ ...prev, organization, employeeId: '' }));
  };

  // ── 5. Per-step validation ───────────────────────────────────
  const validateStep = (stepIndex) => {
    const { requiredFields } = STEPS[stepIndex - 1];

    const normPhone = (v) => String(v ?? '').replace(/\D/g, '');

    for (const field of requiredFields) {
      const v = formData[field];
      if (v === '' || v === null || v === undefined)
        return `${FIELD_LABELS[field] || field} is required`;
    }

    // Mobile and emergency contact must be different (validate on any step once both are present)
    {
      const mobile = normPhone(formData.mobileNumber);
      const emergency = normPhone(formData.emergencyContactNumber);
      if (mobile && emergency && mobile === emergency) {
        return 'Mobile number and Emergency contact number cannot be the same.';
      }
    }

    if (stepIndex === 1) {
      if (formData.password.length < 6)
        return 'Password must be at least 6 characters.';
      if (!['male', 'female', 'other'].includes(formData.gender))
        return 'Please select a valid gender.';
      if (!['single', 'married'].includes(formData.maritalStatus))
        return 'Please select a valid marital status.';
    }

    if (stepIndex === 3) {
      for (const f of NON_NEGATIVE_NUMBER_FIELDS) {
        if (formData[f] !== '' && Number(formData[f]) < 0)
          return `${FIELD_LABELS[f] || f} cannot be negative.`;
      }
      for (const f of PAYROLL_PERCENTAGE_INPUT_FIELDS) {
        if (formData[f] === '')
          return `${FIELD_LABELS[f] || f} is required`;
        if (Number(formData[f]) < 0)
          return `${FIELD_LABELS[f] || f} cannot be negative.`;
      }
    }

    if (stepIndex === 4) {
      for (const f of YEAR_FIELDS) {
        const y = formData[f];
        if (y !== '' && (Number(y) < 1900 || Number(y) > 2100))
          return 'Year must be between 1900 and 2100.';
      }
    }

    return null;
  };

  const handleNext = async () => {
    const err = validateStep(currentStep);
    if (err) { toast.error(err); return; }

    if (currentStep === 6) {
      try {
        setSubmitting(true);
        const response = await api.post('/onboarding/employee', buildPayload());
        const createdEmployee = response.data.employee || response.data;
        const createdEmployeeId = getUserIdentifier(createdEmployee);
        const createdEmployeeTempId = createdEmployee.tempEmployeeId || createdEmployee.employeeId || '';

        let offerLetterOk = false;
        if (signedOfferLetterFile && createdEmployeeId) {
          try {
            const fd = new FormData();
            fd.append('file', signedOfferLetterFile);
            fd.append('documentType', 'offer_letter');
            await api.post(`/documents/employee/${createdEmployeeId}/upload`, fd, uploadRequestConfig());
            offerLetterOk = true;
          } catch (uploadErr) {
            console.error('Offer letter upload failed:', uploadErr);
            toast.warning(
              uploadErr.response?.data?.message ||
                'Employee was created but the signed offer letter could not be uploaded. Add it on the Documents step.',
            );
          }
        }

        setSignedOfferLetterFile(null);

        toast.success(
          offerLetterOk
            ? 'Employee created with signed offer letter saved. Pending Super Admin approval — add any remaining documents below.'
            : 'Employee created — pending Super Admin approval. You can upload documents below.',
        );

        // Update refs first — synchronous, available immediately
        newEmployeeIdRef.current = createdEmployeeId;
        newEmployeeNameRef.current = createdEmployee.name;

        // Then update state + step together
        setNewEmployeeId(createdEmployeeId);
        setNewEmployeeName(createdEmployee.name);
        setNewEmployeeTempId(createdEmployeeTempId);
        setCurrentStep(7);

      } catch (error) {
        console.error('Employee creation failed:', error);
        const errData = error.response?.data;
        const errMsg =
          errData?.errors?.[0]?.msg ||
          errData?.message ||
          'Failed to create employee.';
        toast.error(errMsg);
      } finally {
        setSubmitting(false);
      }
    } else {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  // ── 6. Payload builder ───────────────────────────────────────
  const buildPayload = () => {
    const payload = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(key)) return;
      if (key === 'employeeId' && !String(value || '').trim()) return;
      if (typeof value === 'boolean') { payload[key] = value; return; }
      if (key === 'internshipDays') {
        payload[key] = value === '' || value === null || value === undefined ? 90 : Number(value);
        return;
      }
      payload[key] = NUMERIC_FIELDS.includes(key) ? Number(value) : value;
    });
    return payload;
  };

  // ── 7. Final submission ──────────────────────────────────────
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (currentStep === STEPS.length) {
      try {
        setSubmitting(true);
        const result = await documentsStepRef.current?.saveAllPending?.();
        if (result?.failed) {
          toast.error(`${result.failed} document(s) could not be saved. Please click Save on those rows and retry Finish.`);
          return;
        }
        if (result?.saved) {
          toast.success(`${result.saved} document(s) saved successfully`);
        }
      } finally {
        setSubmitting(false);
      }

      toast.success('Employee created — pending Super Admin approval');
      setLoading(true);
      fetchEmployees();
      closeCreateModal();
      return;
    }

    toast.error('Please complete all steps first.');
  };

  // ── 8. Derived values ────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) =>
      emp.name?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.tempEmployeeId?.toLowerCase().includes(term) ||
      emp.organization?.toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  const activeCount   = employees.filter((e) => e.isActive).length;
  const inactiveCount = employees.length - activeCount;

  const stepProps = {
    formData,
    handleInputChange,
    handleOrganizationChange,
    signedOfferLetterFile,
    onSignedOfferLetterChange: setSignedOfferLetterFile,
    employeeId: newEmployeeId,
    employeeName: newEmployeeName,
    tempEmployeeId: newEmployeeTempId,
    jobTitle: formData.jobTitle,
  };
  const ActivePanel = STEP_PANELS[currentStep - 1];

  // ──────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* PAGE HEADER */}
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employee Onboarding</h1>
          <p className="mt-1 text-sm text-slate-600">
            Add new employees using the onboarding form. Only pending onboarding records are listed below.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setLoading(true); fetchEmployees(); }}
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add New Employee
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Employees</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{employees.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Active</p>
          <p className="mt-2 text-2xl font-semibold text-emerald-600">{activeCount}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Inactive</p>
          <p className="mt-2 text-2xl font-semibold text-rose-600">{inactiveCount}</p>
        </div>
      </div>

      {/* SEARCH */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email, temp ID, or organization"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* EMPLOYEE LIST TABLE */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Pending Onboarding Employees ({filteredEmployees.length})
          </h3>
        </div>

        {loading ? (
          <LoadingSpinner size="lg" className="py-8" />
        ) : filteredEmployees.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">No employee records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Temp ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Organization</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredEmployees.map((employee) => (
                  <tr key={getUserIdentifier(employee) || employee.email} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="mr-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary-100">
                          <Users className="h-4 w-4 text-primary-700" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{employee.name}</p>
                          <p className="text-xs text-slate-500">
                            <Mail className="mr-1 inline h-3 w-3" />
                            {employee.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{employee.tempEmployeeId || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <Building2 className="mr-1 inline h-4 w-4 text-slate-400" />
                      {employee.organization || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        employee.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FOOTER LINK */}
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        <p>
          Existing employee editing is available on the{' '}
          <Link to="/admin/employees" className="font-medium text-primary-700 underline">
            Employees page
          </Link>.
        </p>
      </div>

      {/* ─── ONBOARDING MODAL ─── */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 px-4 py-8">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col">

            {/* Title bar */}
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 flex-shrink-0">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">New Employee Onboarding</h2>
                <p className="text-sm text-slate-500">
                  Step {currentStep} of {STEPS.length} —{' '}
                  <span className="font-medium text-slate-700">
                    {STEPS[currentStep - 1].description}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeCreateModal}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Stepper progress bar */}
            <div className="flex-shrink-0">
              <StepHeader currentStep={currentStep} />
            </div>

            {/* Active step panel */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="overflow-y-auto px-6 py-5 flex-1">
                {currentStep === STEPS.length ? (
                  <StepDocuments ref={documentsStepRef} {...stepProps} />
                ) : (
                  <ActivePanel {...stepProps} />
                )}
              </div>

              {/* Back / Next / Submit */}
              <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 flex-shrink-0">

                {/* ✅ FIX 3: Back button hidden on Step 6 — employee is already
                    created in DB, going back could trigger a duplicate creation */}
                {currentStep < STEPS.length ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </button>
                ) : (
                  <div />
                )}

                <span className="text-xs text-slate-400">
                  {currentStep < STEPS.length
                    ? 'Fill required (*) fields or click Next.'
                    : 'Documents uploaded. Ready to finish.'}
                </span>

                {currentStep < STEPS.length ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={submitting}
                    className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Creating…' : <>Next <ChevronRight className="ml-1 h-4 w-4" /></>}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center rounded-lg bg-emerald-600 px-5 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Finishing…' : 'Finish Onboarding'}
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      )}

      {/* ─── DOCUMENT UPLOAD MODAL ─── */}
      {showDocumentModal && newEmployeeId && (
        <DocumentUploadModal
          employeeId={newEmployeeId}
          employeeName={newEmployeeName}
          onClose={closeDocumentModal}
        />
      )}

    </div>
  );
};

export default ManualOnboarding;